import {
  FilmSlateIcon,
  ImageSquareIcon,
  MusicNotesIcon,
  PlayCircleIcon,
  TextAaIcon,
} from "@phosphor-icons/react";
import { SlackLogoIcon } from "@phosphor-icons/react/dist/csr/SlackLogo";
import { Alert, Button, Flex, Modal, Typography } from "antd";
import { usePublicMediaAssets, useTranslate } from "#src/hooks";
import { UploadRefs } from "../hooks/useBrandkitEdit";
import { ReactNode, useCallback, useState } from "react";
import {
  BrandKitAsset,
  BrandKitAssetResponse,
  MediaAsset,
} from "pages/media/types";
import { StagedBrandKitAsset } from "types/";
import { FontPurposes } from "../types";
import {
  AssetCategory,
  AssetType,
  AssetUploader,
  MIME_TYPES,
} from "./preview/AssetUploader";
import { isPublicFont } from "../helpers";
import { PreviewTitle } from "./preview/PreviewTitle";
import styled from "styled-components";

export type BrandKitAssetResponseWithTypedPurpose = Omit<
  BrandKitAssetResponse,
  "purpose"
> & {
  purpose: AssetPurpose;
};

export const PicturePurpose = [
  "Logo" as const,
  "Background" as const,
  "Banner" as const,
];

export const VideoPurposes = [
  "Introduction" as const,
  "Transition" as const,
  "Background" as const,
  "Closing" as const,
];

export const MusicPurposes = ["Music" as const];

export type AssetPurpose =
  | (typeof MusicPurposes)[number]
  | (typeof VideoPurposes)[number]
  | (typeof FontPurposes)[number]
  | (typeof PicturePurpose)[number];

enum ButtonType {
  Logo = "logo",
  Fonts = "fonts",
  Background = "background",
  Music = "music",
  Transition = "transition",
  IntroOutro = "introOutro",
}

// Configuration for each uploader section
const UPLOADER_CONFIGS: Record<
  ButtonType,
  {
    purposes: AssetPurpose[];
    assetType: AssetType;
    category: AssetCategory;
    mimeTypes: string;
  }
> = {
  [ButtonType.Logo]: {
    purposes: [PicturePurpose[0], PicturePurpose[2]] as AssetPurpose[], // Logo, Banner
    assetType: "Picture" as const,
    category: "AssetImage" as const,
    mimeTypes: MIME_TYPES.image,
  },
  [ButtonType.Background]: {
    // Special handling in renderUploaders - shows both Image and Video Background uploaders
    purposes: [PicturePurpose[1]] as AssetPurpose[], // Background (Image)
    assetType: "Picture" as const,
    category: "AssetImage" as const,
    mimeTypes: MIME_TYPES.image,
  },
  [ButtonType.Transition]: {
    purposes: [VideoPurposes[1]] as AssetPurpose[], // Transition only
    assetType: "Video" as const,
    category: "Video" as const,
    mimeTypes: MIME_TYPES.video,
  },
  [ButtonType.IntroOutro]: {
    purposes: [VideoPurposes[0], VideoPurposes[3]] as AssetPurpose[], // Introduction, Closing
    assetType: "Video" as const,
    category: "Video" as const,
    mimeTypes: MIME_TYPES.video,
  },
  [ButtonType.Music]: {
    purposes: ["Music"] as AssetPurpose[],
    assetType: "Music" as const,
    category: "Music" as const,
    mimeTypes: MIME_TYPES.music,
  },
  [ButtonType.Fonts]: {
    purposes: FontPurposes, // Title, Subtitles, Text
    assetType: "Font" as const,
    category: "Font" as const,
    mimeTypes: MIME_TYPES.font,
  },
} as const;

export const BrandKitAssets = ({
  brandKitAssets,
  uploadRefs,
  organizationId,
  stagedAssets,
  setStagedAssets,
  onAssetDeleteSuccess,
  onFontDeleted,
  updateFontStateOptimistically,
  brandKitId,
}: {
  brandKitAssets: BrandKitAsset[];
  uploadRefs: UploadRefs;
  organizationId: string;
  stagedAssets: StagedBrandKitAsset[];
  setStagedAssets: React.Dispatch<React.SetStateAction<StagedBrandKitAsset[]>>;
  onAssetDeleteSuccess: (assetId: string) => void;
  onFontDeleted?: (assetId: string) => void | Promise<void>;
  updateFontStateOptimistically?: (
    purpose: string,
    update: { asset_id: string; description?: string; path?: string } | null
  ) => void;
  brandKitId: string;
}) => {
  const t = useTranslate();
  const [assetType, setAssetType] = useState<ButtonType | null>(null);
  const { publicFonts } = usePublicMediaAssets();

  // Tracks which upload slots (keyed by type+purpose) are currently uploading,
  // so the modal below can be locked from closing until they all finish.
  const [uploadingSlots, setUploadingSlots] = useState<Set<string>>(new Set());
  const handleUploadingChange = useCallback(
    (slotKey: string, isUploading: boolean) => {
      setUploadingSlots((prev) => {
        const next = new Set(prev);
        if (isUploading) {
          next.add(slotKey);
        } else {
          next.delete(slotKey);
        }
        return next;
      });
    },
    []
  );
  const isAnyUploading = uploadingSlots.size > 0;

  const music = brandKitAssets.filter((asset) => asset.type === "Music");
  const videos = brandKitAssets.filter((asset) => asset.type === "Video");
  const images = brandKitAssets.filter((asset) => asset.type === "Picture");
  // Fonts are identified by purpose, not type (same as edit.tsx)
  const fonts = brandKitAssets.filter(
    (asset) =>
      asset.purpose === "Title" ||
      asset.purpose === "Subtitles" ||
      asset.purpose === "Text"
  );

  // Also check `stagedAssets`, not just the server-confirmed `brandKitAssets` —
  // assets uploaded this session (immediately, on drop) aren't reflected in
  // `brandKitAssets` until the next fetch, since we deliberately don't
  // refetch after each individual upload (would reset unsaved color edits).
  const hasVideoBackground =
    videos.some((video) => video.purpose === "Background") ||
    stagedAssets.some((s) => s.type === "Video" && s.purpose === "Background");
  const hasImageBackground =
    images.some((image) => image.purpose === "Background") ||
    stagedAssets.some(
      (s) => s.type === "Picture" && s.purpose === "Background"
    );

  const renderBackgroundHint = (): ReactNode => {
    const alreadyHasBackground = hasVideoBackground || hasImageBackground;
    const type = hasImageBackground ? "video" : "image";
    if (alreadyHasBackground) {
      return (
        <Alert
          message={t(`brandkit.components.backgroundHint.${type}`)}
          type="warning"
          showIcon
        />
      );
    }
    return <></>;
  };

  const getUploadRef = (type: ButtonType, purpose: AssetPurpose) => {
    switch (type) {
      case ButtonType.Logo:
      case ButtonType.Background:
        return uploadRefs.image[purpose as keyof typeof uploadRefs.image];
      case ButtonType.Transition:
      case ButtonType.IntroOutro:
        return uploadRefs.video[purpose as keyof typeof uploadRefs.video];
      case ButtonType.Music:
        return uploadRefs.music.Music;
      case ButtonType.Fonts:
        return uploadRefs.font[purpose as keyof typeof uploadRefs.font];
      default:
        return undefined;
    }
  };

  const getAssetsForType = (type: ButtonType) => {
    switch (type) {
      case ButtonType.Logo:
      case ButtonType.Background:
        return images;
      case ButtonType.Transition:
      case ButtonType.IntroOutro:
        return videos;
      case ButtonType.Music:
        return music;
      case ButtonType.Fonts:
        return fonts;
      default:
        return [];
    }
  };

  const getHintForPurpose = (type: ButtonType, purpose: AssetPurpose) => {
    if (purpose !== "Background") return <></>;
    if (type === ButtonType.Background) return renderBackgroundHint();
    return <></>;
  };

  const handleFontDelete = async (assetId: string) => {
    try {
      await onFontDeleted?.(assetId);
    } catch (error) {
      console.error("Error setting default font after delete", error);
    }
  };
  const renderUploaders = (type: ButtonType) => {
    const config = UPLOADER_CONFIGS[type];
    const assets = getAssetsForType(type);

    if (type === ButtonType.Background) {
      // Special handling for Background - show both Image and Video Background uploaders
      // Only one background type can be staged at a time
      const handleBackgroundAdded = (otherRef: "image" | "video") => {
        // Clears any not-yet-saved upload staged in the sibling slot this
        // session (AssetUploader's own reset now also drops its locally
        // cached "just uploaded" asset, not just the shared file list).
        uploadRefs[otherRef].Background.current?.reset();

        // Image/video Background share one purpose, so the backend replaces
        // whichever one already existed from a previous session. Patch that
        // out of the local cache immediately (no refetch, so it can't reset
        // any unsaved name/color edits) instead of waiting for a reload.
        const staleAsset = (otherRef === "image" ? images : videos).find(
          (asset) => asset.purpose === "Background"
        );
        if (staleAsset) {
          onAssetDeleteSuccess(staleAsset.asset_id);
        }
      };

      return (
        <>
          <Flex
            vertical
            gap={10}
            style={{
              flex: "1 1 200px",
              minWidth: 200,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 20 }}>
              {t("brandkit.components.BrandKitAssets.ImageBackground")}
            </Typography.Text>
            <AssetUploader
              config={{
                assetType: "Picture",
                category: "AssetImage",
                acceptedMimeTypes: MIME_TYPES.image,
                purposeValue: "Background",
              }}
              assets={images}
              stagedAssets={stagedAssets}
              setStagedAssets={setStagedAssets}
              onAssetDeleteSuccess={onAssetDeleteSuccess}
              organizationId={organizationId}
              uploadRef={uploadRefs.image.Background}
              hint={renderBackgroundHint()}
              brandKitId={brandKitId}
              onUploadComplete={() => handleBackgroundAdded("video")}
              onUploadingChange={(isUploading) =>
                handleUploadingChange("background-image", isUploading)
              }
            />
          </Flex>
          <Flex
            vertical
            gap={10}
            style={{
              flex: "1 1 200px",
              minWidth: 200,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 20 }}>
              {t("brandkit.components.BrandKitAssets.VideoBackground")}
            </Typography.Text>
            <AssetUploader
              config={{
                assetType: "Video",
                category: "Video",
                acceptedMimeTypes: MIME_TYPES.video,
                purposeValue: "Background",
              }}
              assets={videos}
              stagedAssets={stagedAssets}
              setStagedAssets={setStagedAssets}
              onAssetDeleteSuccess={onAssetDeleteSuccess}
              organizationId={organizationId}
              uploadRef={uploadRefs.video.Background}
              hint={renderBackgroundHint()}
              brandKitId={brandKitId}
              onUploadComplete={() => handleBackgroundAdded("image")}
              onUploadingChange={(isUploading) =>
                handleUploadingChange("background-video", isUploading)
              }
            />
          </Flex>
        </>
      );
    }

    if (type === ButtonType.Fonts) {
      const handleFontDeleteForPurpose =
        (purpose: string) => (assetId: string) => {
          onAssetDeleteSuccess(assetId);
          updateFontStateOptimistically?.(purpose, null);
          handleFontDelete(assetId).catch((error) =>
            console.error("Error deleting font", error)
          );
        };

      return config.purposes.map((purpose) => {
        const existingFont = fonts.find((f) => f.purpose === purpose);

        // Only hides the *previously saved* font preview (e.g. a public/default
        // font) so the dropzone shows instead. Must not depend on staged/upload
        // state — AssetUploader itself always shows progress and the freshly
        // uploaded font regardless of this flag.
        const showFontPreview =
          existingFont &&
          !isPublicFont(existingFont.asset_id, publicFonts as MediaAsset[]);

        return (
          <Flex
            key={purpose}
            vertical
            gap={10}
            style={{ flex: "1 1 200px", minWidth: 200 }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 20 }}>
              {t(`brandkit.components.BrandKitAssets.${purpose}`)}
            </Typography.Text>
            <AssetUploader
              config={{
                assetType: config.assetType,
                category: config.category,
                acceptedMimeTypes: config.mimeTypes,
                purposeValue: purpose,
              }}
              assets={assets}
              stagedAssets={stagedAssets}
              setStagedAssets={setStagedAssets}
              onAssetDeleteSuccess={handleFontDeleteForPurpose(purpose)}
              organizationId={organizationId}
              uploadRef={getUploadRef(type, purpose)}
              showPreviewOverride={showFontPreview}
              onUploadSuccess={(asset) =>
                updateFontStateOptimistically?.(purpose, {
                  asset_id: asset.asset_id,
                  description: asset.name,
                  path: asset.path,
                })
              }
              onUploadingChange={(isUploading) =>
                handleUploadingChange(`${type}-${purpose}`, isUploading)
              }
              brandKitId={brandKitId}
            />
          </Flex>
        );
      });
    }

    return config.purposes.map((purpose) => (
      <Flex
        key={purpose}
        vertical
        gap={10}
        style={{
          flex: "1 1 200px",
          minWidth: 200,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        {config.purposes.length > 1 && (
          <Typography.Text type="secondary" style={{ fontSize: 20 }}>
            {t(`brandkit.components.BrandKitAssets.${purpose}`)}
          </Typography.Text>
        )}
        <AssetUploader
          config={{
            assetType: config.assetType,
            category: config.category,
            acceptedMimeTypes: config.mimeTypes,
            purposeValue: purpose,
          }}
          assets={assets}
          stagedAssets={stagedAssets}
          setStagedAssets={setStagedAssets}
          onAssetDeleteSuccess={onAssetDeleteSuccess}
          organizationId={organizationId}
          uploadRef={getUploadRef(type, purpose)}
          hint={getHintForPurpose(type, purpose)}
          onUploadingChange={(isUploading) =>
            handleUploadingChange(`${type}-${purpose}`, isUploading)
          }
          brandKitId={brandKitId}
        />
      </Flex>
    ));
  };

  const itemButtons = [
    {
      type: ButtonType.Logo,
      icon: <SlackLogoIcon size={20} />,
    },
    {
      type: ButtonType.Fonts,
      icon: <TextAaIcon size={20} />,
    },
    {
      type: ButtonType.Background,
      icon: <ImageSquareIcon size={20} />,
    },
    {
      type: ButtonType.Music,
      icon: <MusicNotesIcon size={20} />,
    },
    {
      type: ButtonType.Transition,
      icon: <PlayCircleIcon size={20} />,
    },
    {
      type: ButtonType.IntroOutro,
      icon: <FilmSlateIcon size={20} />,
    },
  ];

  return (
    <Flex vertical gap={20} style={{ width: "100%" }}>
      <PreviewTitle title={t("media.brandkit.edit.brandItems")} />
      <Flex wrap gap={20} style={{ alignContent: "stretch" }}>
        {itemButtons.map((item) => (
          <StyledAssetTypeButton
            key={item.type}
            size="large"
            onClick={() => setAssetType(item.type)}
          >
            <Flex align="center" gap={10} className="asset-type-button-content">
              {item.icon}
              <Typography.Title
                level={5}
                className="asset-type-title"
                style={{ margin: 0, fontWeight: 400 }}
                type="secondary"
              >
                {t(
                  `brandkit.components.BrandKitAssets.buttons.${item.type}.title`
                )}
              </Typography.Title>
            </Flex>
          </StyledAssetTypeButton>
        ))}
      </Flex>
      <Modal
        open={assetType !== null}
        onCancel={() => {
          if (!isAnyUploading) setAssetType(null);
        }}
        title={
          assetType
            ? t(`brandkit.components.BrandKitAssets.buttons.${assetType}.title`)
            : undefined
        }
        footer={null}
        centered
        width={720}
        destroyOnHidden={false}
        closable={!isAnyUploading}
        maskClosable={!isAnyUploading}
        keyboard={!isAnyUploading}
        transitionName=""
        maskTransitionName=""
        styles={{
          header: { padding: "20px 24px 16px" },
          body: {
            padding: 24,
            maxHeight: "70vh",
            overflowY: "auto",
          },
        }}
      >
        {itemButtons.map((item) => (
          <Flex
            key={item.type}
            wrap
            gap={20}
            // Keep uploaders mounted to persist file selection state between opens
            style={{ display: assetType === item.type ? "flex" : "none" }}
          >
            {renderUploaders(item.type)}
          </Flex>
        ))}
      </Modal>
    </Flex>
  );
};

const StyledAssetTypeButton = styled(Button)`
  flex: 1 1 30%;
  min-width: 170px;
  height: auto;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 10px;

  .asset-type-button-content {
    color: ${({ theme }) => theme.colorTextSecondary};
    transition: color 0.2s ease;
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    border-color: ${({ theme }) => theme.colorPrimary} !important;

    .asset-type-button-content,
    .asset-type-title.ant-typography-secondary {
      color: ${({ theme }) => theme.colorPrimary} !important;
    }
  }
`;
